class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay, .drawer__close')) this.close();
    });
    this.installCartLinkInterceptor();
  }

  installCartLinkInterceptor() {
    if (window.__CART_DRAWER_LINK_INTERCEPTOR__) return;
    window.__CART_DRAWER_LINK_INTERCEPTOR__ = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button > 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (_) {
        return;
      }

      const cartPath = `${window.Shopify?.routes?.root || '/'}cart`.replace(/\/+/g, '/');
      if (url.origin !== window.location.origin ||
          url.pathname.replace(/\/$/, '') !== cartPath.replace(/\/$/, '')) return;

      event.preventDefault();
      document.querySelector('cart-drawer')?.open(link);
    });
  }

  open(triggeredBy, cartAlreadyRefreshed = false) {
    if (!cartAlreadyRefreshed) window.dvikRestoreDrawerView?.(this);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const summary = this.querySelector('[id^="Details-"] summary');
    if (summary && !summary.hasAttribute('role')) this.setSummaryAccessibility(summary);

    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    if (!cartAlreadyRefreshed && !this.openingCart) {
      this.setAttribute('aria-busy', 'true');
      this.openingCart = Promise.resolve().then(() =>
        window.dvikRefreshDrawer ? window.dvikRefreshDrawer() : window.dvikRefreshCartUi?.()
      ).catch((error) => {
        console.warn('[DVIK cart] Drawer refresh failed', error);
      }).finally(() => {
        this.openingCart = null;
        this.removeAttribute('aria-busy');
      });
    }
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', 'false');
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(state) {
    window.dvikCartCount?.rememberCart(state);
    window.__DVIK_CART_RENDER_VERSION__ = (window.__DVIK_CART_RENDER_VERSION__ || 0) + 1;
    this.productId = state.id;
    const drawerHtml = state.sections?.['cart-drawer'];
    if (drawerHtml) {
      const freshDrawer = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('cart-drawer');
      if (freshDrawer) this.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
    }
    this.getSectionsToRender().forEach((section) => {
      const html = state.sections?.[section.id];
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!html || !target) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(
        section.selector || '.shopify-section'
      );
      if (source) target.innerHTML = source.innerHTML;
    });
    window.dvikCartCount?.fromHeader();
    window.dvikSaveDrawerView?.(this);
    this.open(undefined, true);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

// ---------------------------------------------------------------------------
// Auto-open handling (FIXED)
// Previously the drawer opened on every page load that had a checkout referrer
// or a leftover flag, even when the cart was empty or the page was reloaded.
// Now it only auto-opens when the cart actually contains items.
// ---------------------------------------------------------------------------
(function () {
  const root = `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');

  // Only auto-open when the cart really has items.
  async function cartHasItems() {
    try {
      const res = await fetch(`${root}cart.js?t=${Date.now()}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      });
      if (!res.ok) return false;
      const cart = await res.json();
      return Number(cart.item_count) > 0;
    } catch (_) {
      return false;
    }
  }

  async function autoOpenDrawer(delay) {
    if (!(await cartHasItems())) return;
    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }, delay);
  }

  document.addEventListener('DOMContentLoaded', function () {
    const navType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;

    // Returning from checkout: only on a fresh navigation (not reload,
    // not back/forward) and only if the cart still has items.
    if (navType === 'navigate' && document.referrer && document.referrer.includes('/checkout')) {
      autoOpenDrawer(500);
    }

    // One-time flag set by other scripts (e.g. after add to cart).
    let flagged = false;
    try {
      flagged = Boolean(sessionStorage.getItem('openCartDrawer'));
      if (flagged) sessionStorage.removeItem('openCartDrawer');
    } catch (_) {}
    if (flagged) autoOpenDrawer(300);
  });

  // Global function to open cart drawer from anywhere (user-initiated, so it
  // is fine to open even when the cart is empty).
  window.openCartDrawer = function () {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      const cartIcon = document.querySelector('.header__icon--cart, .cart-icon');
      if (cartIcon) cartIcon.click();
    }
  };
})();
